/**
 * DeGiroSpikeModal.jsx
 * DAS-2573: "DeGiro spike" — type a stock symbol and pull, straight from the
 * DeGiro API, its derivatives (turbos/leveraged + options) and a near-realtime
 * quote (falls back to the EOD close when realtime isn't available on the
 * account's data subscription).
 *
 * Each search performs ONE live DeGiro login server-side. DeGiro locks the
 * account after a few failed logins, so the Search button is disabled while a
 * request is in flight — don't hammer it.
 */
import React, { useState } from 'react';
import { Modal, Input, Button, Table, Tag, Alert, Spin, Descriptions } from 'antd';
import api from '../functions/api';

const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString('nl-NL'));

const derivativeColumns = [
  { title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true },
  { title: 'Symbol', dataIndex: 'symbol', key: 'symbol', width: 90 },
  { title: 'Lev.', dataIndex: 'leverage', key: 'leverage', width: 60, align: 'right',
    render: (v) => (v == null ? '—' : `${v}×`) },
  { title: 'L/S', dataIndex: 'shortlong', key: 'shortlong', width: 50 },
  { title: 'Close', dataIndex: 'close_price', key: 'close_price', width: 90, align: 'right',
    render: fmtNum },
  { title: 'Stop loss', dataIndex: 'stop_loss_price', key: 'stop_loss_price', width: 90,
    align: 'right', render: fmtNum },
  { title: 'Ccy', dataIndex: 'currency', key: 'currency', width: 60 },
];

const optionColumns = [
  { title: 'C/P', dataIndex: 'side', key: 'side', width: 50,
    render: (s) => (s === 'C' ? <Tag color="blue">C</Tag> : s === 'P' ? <Tag color="volcano">P</Tag> : '—') },
  { title: 'Strike', dataIndex: 'strike', key: 'strike', width: 90, align: 'right', render: fmtNum },
  { title: 'Expiry', dataIndex: 'expiration_date', key: 'expiration_date', width: 110 },
  { title: 'Bid', dataIndex: 'bid', key: 'bid', width: 80, align: 'right', render: fmtNum },
  { title: 'Ask', dataIndex: 'ask', key: 'ask', width: 80, align: 'right', render: fmtNum },
  { title: 'Mid (premium)', dataIndex: 'mid', key: 'mid', width: 110, align: 'right',
    render: (v) => (v == null ? '—' : <strong>{fmtNum(v)}</strong>) },
  { title: 'Saxo Uic', dataIndex: 'uic', key: 'uic', width: 100, align: 'right' },
];

const DeGiroSpikeModal = ({ isVisible, onClose, userId }) => {
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const search = async () => {
    const sym = symbol.trim();
    if (!sym || !userId) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const resp = await api.get(`/degiro_spike/${userId}`, { params: { symbol: sym } });
      setData(resp.data);
    } catch (err) {
      console.error('DAS-2573: degiro_spike fetch failed:', err);
      setError(err.response?.data?.error || err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const q = data?.quote;
  const u = data?.underlying;

  return (
    <Modal title="DeGiro spike — derivatives & realtime" open={isVisible}
           onCancel={onClose} footer={null} width={960} destroyOnClose>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <Input
          placeholder="Stock symbol, e.g. AAPL"
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          onPressEnter={search}
          style={{ maxWidth: 260 }}
          allowClear
        />
        <Button type="primary" onClick={search} loading={loading} disabled={!symbol.trim()}>
          Search
        </Button>
      </div>
      <div style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>
        Each search does one live DeGiro login server-side (can take ~10–20s).
        Realtime vs ~15-min-delayed depends on your DeGiro data subscription.
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <Spin tip="Querying DeGiro…" />
        </div>
      )}
      {error && <Alert type="error" showIcon message="DeGiro request failed" description={error} />}

      {data && !loading && !error && !data.found && (
        <Alert type="warning" showIcon message={data.message || 'Not found'} />
      )}

      {data && !loading && !error && data.found && (
        <div>
          <Descriptions size="small" bordered column={2} style={{ marginBottom: 16 }}
            title={`${u?.name || ''} (${u?.symbol || data.symbol})`}>
            <Descriptions.Item label="Price">
              {fmtNum(q?.last_price)} {u?.currency || ''}{' '}
              {data.quote_is_realtime
                ? <Tag color="green">realtime</Tag>
                : <Tag color="orange">EOD close{u?.close_price_date ? ` ${u.close_price_date}` : ''}</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Bid / Ask">
              {fmtNum(q?.bid_price)} / {fmtNum(q?.ask_price)}
            </Descriptions.Item>
            <Descriptions.Item label="ISIN">{u?.isin || '—'}</Descriptions.Item>
            <Descriptions.Item label="vwdId">{u?.vwd_id || '—'}</Descriptions.Item>
          </Descriptions>

          <h3 style={{ marginBottom: 4 }}>
            Leveraged / turbos <Tag>{data.counts?.leveraged ?? 0}</Tag>
          </h3>
          {data.leveraged_shown < (data.counts?.leveraged ?? 0) && (
            <div style={{ color: '#888', fontSize: 12, marginBottom: 6 }}>
              Showing first {data.leveraged_shown} of {data.counts.leveraged}.
            </div>
          )}
          <Table dataSource={data.leveraged || []} columns={derivativeColumns}
                 rowKey={(r, i) => `lev-${r.id ?? i}`} pagination={{ pageSize: 10 }} size="small" />

          <h3 style={{ marginTop: 16, marginBottom: 4 }}>
            Options <Tag>{data.counts?.options ?? 0}</Tag>
            {data.options_source === 'saxo' && <Tag color="geekblue">Saxo</Tag>}
            {data.options_delayed_minutes ? (
              <Tag color="orange">delayed {data.options_delayed_minutes}m</Tag>
            ) : null}
            {data.options_exchange && (
              <span style={{ color: '#888', fontSize: 12, fontWeight: 'normal' }}>{data.options_exchange}</span>
            )}
          </h3>
          {(!data.options || data.options.length === 0) && data.options_note ? (
            <Alert type="info" showIcon message={data.options_note} style={{ marginBottom: 8 }} />
          ) : (
            <>
              {data.options_shown < (data.counts?.options ?? 0) && (
                <div style={{ color: '#888', fontSize: 12, marginBottom: 6 }}>
                  Showing nearest {data.options_shown} of {data.counts.options} contracts.
                </div>
              )}
              <Table dataSource={data.options || []} columns={optionColumns}
                     rowKey={(r, i) => `opt-${r.id ?? i}`} pagination={{ pageSize: 10 }} size="small" />
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default DeGiroSpikeModal;
